import type { DashboardDeadlineRow } from "./studentDashboard";

/**
 * Build an RFC-5545 iCalendar (.ics) document from a list of deliverable
 * deadlines and trigger a browser download (web only). Each deliverable
 * becomes a 30-minute VEVENT pinned to its `dueAt`. Calendar apps that
 * import the file (Google Calendar, Outlook, Apple Calendar) get the
 * student\'s deliverables as one-shot events.
 *
 * For ongoing auto-sync (where edits in Coravu propagate to the
 * subscribed calendar), we\'ll need a server-side Edge Function that
 * serves a personalised .ics feed at a stable URL — see Settings UI for
 * the placeholder. This function is the immediate, no-backend version.
 */
const PRODID = "-//Coravu//Student Deliverables//EN";

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/** Convert an ISO string into the YYYYMMDDTHHMMSSZ form iCalendar wants. */
function toICalUtc(iso: string): string {
  const d = new Date(iso);
  const y = d.getUTCFullYear();
  const m = pad(d.getUTCMonth() + 1);
  const day = pad(d.getUTCDate());
  const hh = pad(d.getUTCHours());
  const mm = pad(d.getUTCMinutes());
  const ss = pad(d.getUTCSeconds());
  return `${y}${m}${day}T${hh}${mm}${ss}Z`;
}

/** Escape iCalendar text per RFC 5545 §3.3.11. */
function escapeICalText(s: string): string {
  return s
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

/** Stable UID per deliverable so re-imports update the same event. */
function uidFor(row: DashboardDeadlineRow, salt: number): string {
  const slug = `${row.competitionName}-${row.deliverableName}-${row.dueAt}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .slice(0, 80);
  return `coravu-${slug}-${salt}@coravu.com`;
}

export function buildIcsForDeadlines(rows: DashboardDeadlineRow[]): string {
  const stamp = toICalUtc(new Date().toISOString());
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${PRODID}`,
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "X-WR-CALNAME:Coravu Deliverables",
    "X-WR-CALDESC:Upcoming Coravu deliverable deadlines",
  ];
  rows.forEach((row, i) => {
    const startIso = row.dueAt;
    const start = toICalUtc(startIso);
    const endMs = new Date(startIso).getTime() + 30 * 60_000;
    const end = toICalUtc(new Date(endMs).toISOString());
    lines.push(
      "BEGIN:VEVENT",
      `UID:${uidFor(row, i)}`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${escapeICalText(`${row.competitionName} \u2014 ${row.deliverableName}`)}`,
      `DESCRIPTION:${escapeICalText(`Coravu deliverable due. Open the program at coravu.com to submit.`)}`,
      "END:VEVENT",
    );
  });
  lines.push("END:VCALENDAR");
  return lines.join("\r\n") + "\r\n";
}

/** Trigger a browser download of the given .ics content. Web only. */
export function downloadIcs(filename: string, ics: string): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
